const METERS_PER_LATITUDE = 111320;

function asLatLng(point) {
  if (Array.isArray(point)) return { lat: Number(point[0]), lng: Number(point[1]) };
  return { lat: Number(point.lat), lng: Number(point.lng) };
}

function metersBetween(from, to) {
  const x = (to.lng - from.lng) * METERS_PER_LATITUDE * Math.cos((from.lat * Math.PI) / 180);
  const y = (to.lat - from.lat) * METERS_PER_LATITUDE;
  return Math.hypot(x, y);
}

function compassBearing(from, to) {
  const east = (to.lng - from.lng) * Math.cos((from.lat * Math.PI) / 180);
  const north = to.lat - from.lat;
  return ((Math.atan2(east, north) * 180) / Math.PI + 360) % 360;
}

function bearingGap(left, right) {
  const value = Math.abs((((left - right) % 360) + 360) % 360);
  return value > 180 ? 360 - value : value;
}

function closestVertex(coordinates, origin) {
  return coordinates.reduce((best, point, index) => {
    const latlng = asLatLng(point);
    const distance = metersBetween(origin, latlng);
    return !best || distance < best.distance ? { index, distance, point: latlng } : best;
  }, null);
}

function walk(coordinates, startIndex, step, meters) {
  const points = [asLatLng(coordinates[startIndex])];
  let travelled = 0;
  for (let index = startIndex; travelled < meters; index += step) {
    const nextIndex = index + step;
    if (nextIndex < 0 || nextIndex >= coordinates.length) break;
    const next = asLatLng(coordinates[nextIndex]);
    travelled += metersBetween(points[points.length - 1], next);
    points.push(next);
  }
  return points;
}

/** The part of a road that leaves the intersection along `awayBearing`. */
function sliceAway(road, origin, awayBearing, meters) {
  const coordinates = road?.co || [];
  if (coordinates.length < 2) return [];
  const closest = closestVertex(coordinates, origin);
  if (!closest || closest.distance > 80) return [];
  const forward = walk(coordinates, closest.index, 1, Math.min(meters, 25));
  const backward = walk(coordinates, closest.index, -1, Math.min(meters, 25));
  const forwardGap = bearingGap(compassBearing(origin, forward[forward.length - 1]), awayBearing);
  const backwardGap = bearingGap(compassBearing(origin, backward[backward.length - 1]), awayBearing);
  const step = forwardGap <= backwardGap ? 1 : -1;
  return walk(coordinates, closest.index, step, meters);
}

/** Shift a travelled path onto the right-hand side of the carriageway. */
function offsetRight(points, meters) {
  if (points.length < 2) return points;
  return points.map((point, index) => {
    const previous = points[Math.max(0, index - 1)];
    const next = points[Math.min(points.length - 1, index + 1)];
    const right = ((compassBearing(previous, next) + 90) * Math.PI) / 180;
    return {
      lat: point.lat + (Math.cos(right) * meters) / METERS_PER_LATITUDE,
      lng: point.lng + (Math.sin(right) * meters) / (METERS_PER_LATITUDE * Math.cos((point.lat * Math.PI) / 180)),
    };
  });
}

/**
 * A maneuver that follows the GPS path of the incoming street and then the
 * outgoing street, kept on the right-hand side like the directional roads in
 * Assign.
 */
export function maneuverAlongRoads({
  intersection,
  fromRoad,
  toRoad,
  fromBearing,
  toBearing,
  length = 55,
}) {
  const origin = { lat: Number(intersection.lat), lng: Number(intersection.lng) };
  const approach = sliceAway(fromRoad, origin, fromBearing, length).slice().reverse();
  const exit = sliceAway(toRoad, origin, toBearing, length);
  if (approach.length < 2 || exit.length < 2) return [];
  const incoming = offsetRight(approach, 4);
  const outgoing = offsetRight(exit, 4);
  // One shared point at the junction. Joining the two offset copies of that
  // point draws the spur that shows up as a triangle on the satellite.
  return [...incoming.slice(0, -1), origin, ...outgoing.slice(1)];
}

export function leafletPolygonToGeoJSON(latlngs) {
  const ring = (latlngs || []).map((point) => [point.lng, point.lat]);
  if (ring.length > 0) ring.push([...ring[0]]);
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } };
}

export function intersectionsInside(intersections, polygon) {
  if (!polygon) return [];
  const ring = polygon.geometry?.coordinates?.[0] || [];
  return (intersections || []).filter((intersection) => {
    const x = Number(intersection.lng);
    const y = Number(intersection.lat);
    let inside = false;
    for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index++) {
      const [xi, yi] = ring[index];
      const [xj, yj] = ring[previous];
      const crosses = ((yi > y) !== (yj > y))
        && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
      if (crosses) inside = !inside;
    }
    return inside;
  });
}
